import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Mail, Loader2 } from 'lucide-react';
import { REGEXP_ONLY_DIGITS } from 'input-otp';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@/components/ui/input-otp';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from '@/components/ui/sonner';
import { isValidEmail } from '@/lib/validation';
import { useRequestOtp, useVerifyOtp } from './api';

type Step = 'email' | 'otp';

// Fallback when the server doesn't report a length (older API / network error).
const DEFAULT_OTP_LENGTH = 6;

export function LoginPage() {
  const { t } = useTranslation();
  const [step, setStep] = useState<Step>('email');
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [otpLength, setOtpLength] = useState(DEFAULT_OTP_LENGTH);

  const requestOtp = useRequestOtp();
  const verifyOtp = useVerifyOtp();

  const onRequest = (e: React.FormEvent) => {
    e.preventDefault();
    if (!isValidEmail(email)) return;
    requestOtp.mutate(email.trim(), {
      // The server reports how many digits it generated; render slots to match
      // instead of hardcoding, so AUTH_OTP_LENGTH changes don't break login.
      onSuccess: (data) => setOtpLength(data.otpLength ?? DEFAULT_OTP_LENGTH),
      // Always go to step 2: the API returns 200 regardless so the UI must too,
      // otherwise we'd leak whether the email is registered.
      onSettled: () => setStep('otp'),
      onError: () =>
        toast.error(t('login.errors.server_unreachable_title'), {
          description: t('login.errors.server_unreachable_description'),
        }),
    });
  };

  const onVerify = (e: React.FormEvent) => {
    e.preventDefault();
    if (!otp.trim()) return;
    verifyOtp.mutate(
      { email: email.trim(), otp: otp.trim() },
      {
        onError: (err) => {
          toast.error(t('login.errors.invalid_code_title'), {
            description: err.message || t('login.errors.invalid_code_description'),
          });
          setOtp('');
        },
      },
    );
  };

  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-2 text-center">
          <CardTitle className="text-2xl">{t('login.title')}</CardTitle>
          <CardDescription>
            {step === 'email'
              ? t('login.email_step_description')
              : t('login.otp_step_description', { email })}
          </CardDescription>
        </CardHeader>

        <CardContent>
          {step === 'email' ? (
            <form onSubmit={onRequest} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">{t('login.email_label')}</Label>
                <div className="relative">
                  <Mail
                    aria-hidden
                    className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground"
                  />
                  <Input
                    id="email"
                    type="email"
                    autoComplete="email"
                    required
                    placeholder={t('login.email_placeholder')}
                    className="pl-9"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={requestOtp.isPending}
                  />
                </div>
              </div>
              <Button type="submit" className="w-full" disabled={requestOtp.isPending}>
                {requestOtp.isPending && <Loader2 className="animate-spin" />}
                {t('login.send_code')}
              </Button>
            </form>
          ) : (
            <form onSubmit={onVerify} className="space-y-4">
              <div className="flex flex-col items-center space-y-2">
                <Label htmlFor="otp">{t('login.otp_label', { length: otpLength })}</Label>
                <InputOTP
                  id="otp"
                  autoFocus
                  maxLength={otpLength}
                  pattern={REGEXP_ONLY_DIGITS}
                  autoComplete="one-time-code"
                  value={otp}
                  onChange={setOtp}
                  disabled={verifyOtp.isPending}
                >
                  <InputOTPGroup>
                    {Array.from({ length: otpLength }, (_, i) => (
                      <InputOTPSlot key={i} index={i} />
                    ))}
                  </InputOTPGroup>
                </InputOTP>
              </div>
              <Button type="submit" className="w-full" disabled={verifyOtp.isPending}>
                {verifyOtp.isPending && <Loader2 className="animate-spin" />}
                {t('login.verify')}
              </Button>
              <Button
                type="button"
                variant="link"
                className="w-full"
                onClick={() => {
                  setStep('email');
                  setOtp('');
                }}
              >
                {t('login.use_different_email')}
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
      <Link to="/privacy" className="text-xs text-muted-foreground hover:underline">
        {t('login.privacy_policy')}
      </Link>
    </div>
  );
}
